/**
 * Шахматна відомість (ч.1038): дебет (рядки) × кредит (стовпці) за період.
 * Це зведена матриця, а не список — тому власна таблиця `.ledger-matrix`
 * (закріплені перший стовпець і шапка), а не DataTable. Клік по комірці —
 * картка дебетового рахунку з відбором по кредитовому, лише дебет.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports } from '@/api/documents';
import LedgerFilters, { NoRegulatedAccess } from './LedgerFilters';
import { useLedgerParams } from './useLedgerParams';
import { periodLabel } from './ledgerColumns';
import { ledgerLinks, money } from './ledgerModel';

export default function ChessSheetPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const level = st.get('level') || 'leaf';
  const q = useQuery({
    queryKey: ['ledger-chess', st.org, st.from, st.to, level],
    queryFn: () => reports.chessSheet({ organization_id: st.org, date_from: st.from, date_to: st.to, level }),
    enabled: !!st.org,
  });
  const data = q.data;
  if (!st.permsLoading && !st.hasPermission) return <NoRegulatedAccess title="Шахматна відомість" total={st.totalAccessibleOrgs} />;
  const lookup = new Map((data?.cells ?? []).map((c) => [`${c.dt}|${c.cr}`, c.amount]));
  const period = { org: st.org, from: st.from, to: st.to };
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Шахматна відомість{' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span></h2>
      </div>
      <LedgerFilters state={st}
        printHref={ledgerLinks.print('chess', { ...period, level })}
        onExcel={() => reports.ledgerXlsx('/reports/chess-sheet', { organization_id: st.org, date_from: st.from, date_to: st.to, level })}
        disabled={!data}>
        <div className="field">
          <label>Рахунки</label>
          <select value={level} onChange={(e) => st.set({ level: e.target.value === 'leaf' ? null : e.target.value })}>
            <option value="leaf">як у проводках</option>
            <option value="synthetic">синтетичні (2 знаки)</option>
          </select>
        </div>
      </LedgerFilters>
      {q.isError && <p className="ledger__warn">Не вдалося завантажити відомість.</p>}
      {q.isLoading && <p className="ledger__hint">Завантаження…</p>}
      {data && data.cells.length === 0 && <p className="ledger__hint">За період проводок немає.</p>}
      {data && data.cells.length > 0 && (
        <div className="ledger-matrix__wrap">
          <table className="ledger-matrix">
            <thead>
              <tr>
                <th className="ledger-matrix__corner">Дт \ Кт</th>
                {data.cr_accounts.map((c) => <th key={c.code} title={c.name}>{c.code}</th>)}
                <th className="ledger-matrix__total">Разом Дт</th>
              </tr>
            </thead>
            <tbody>
              {data.dt_accounts.map((d) => (
                <tr key={d.code}>
                  <th title={d.name}>{d.code}</th>
                  {data.cr_accounts.map((c) => {
                    const v = lookup.get(`${d.code}|${c.code}`);
                    return (
                      <td key={c.code} className={v ? 'ledger-matrix__cell' : ''}
                        title={v ? `Дт ${d.code} — Кт ${c.code}: картка ${d.code}, лише дебет, кор. ${c.code}` : undefined}
                        onClick={() => { if (v) navigate(ledgerLinks.accountCard({ ...period, account: d.code, corr: c.code, side: 'DT' })); }}>
                        {v ? money(v) : ''}
                      </td>);
                  })}
                  <td className="ledger-matrix__total">{money(data.row_totals[d.code])}</td>
                </tr>
              ))}
              <tr className="ledger-matrix__totals">
                <th>Разом Кт</th>
                {data.cr_accounts.map((c) => <td key={c.code}>{money(data.col_totals[c.code])}</td>)}
                <td className="ledger-matrix__total">{money(data.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {data && <div className="ledger__totals"><span>Рахунків: Дт <strong>{data.dt_accounts.length}</strong> · Кт <strong>{data.cr_accounts.length}</strong> · обороти разом <strong>{money(data.total, { zero: '0,00' })}</strong></span><span className="ledger__hint">клік по комірці — картка дебетового рахунку з відбором по кредитовому</span></div>}
    </div>
  );
}
